import toast, { ToastOptions } from 'react-hot-toast'
import { normalizeApiError } from './apiError'

/**
 * The text an API error actually carries.
 *
 * The API is not consistent about the casing: some endpoints answer with
 * `Message`, others — the pipe delete among them — with `message`. Reading
 * only one of the two produced a toast with nothing in it, so the reason the
 * server refused ("Unable to delete this pipe because it was used by another
 * flow rule") never reached the user, who saw an empty popup and no way to
 * know what to do about it.
 *
 * `fallback`, when a caller passes one, is preferred over the axios text so a
 * screen that has its own translated wording keeps it.
 *
 * `e.message` is deliberately last: on an axios error it is "Request failed
 * with status code 400", which tells the user nothing the server had not
 * already said better.
 */
/**
 * The first field message of a validation error, if the body carries one.
 *
 * A 400 from the API's validator says only "One or more validation errors
 * occurred." in `message`; the reason itself ("'Capacity' must be greater than
 * or equal to '0'.") is in `errors`, keyed by field (`Input.Capacity`). Read on
 * the axios body, and on an already-normalised error that holds `errors` at
 * the top level.
 */
const firstFieldError = (e: any): string | null => {
  const errors =
    e?.response?.data?.errors ?? e?.response?.data?.Errors ?? e?.errors
  if (!errors || typeof errors !== 'object') return null
  for (const messages of Object.values(errors)) {
    const first = Array.isArray(messages) ? messages[0] : messages
    if (typeof first === 'string' && first.trim()) return first
  }
  return null
}

/**
 * Whether sending the same request again could succeed: a network error or a
 * 5xx (the API answers 500 when writes arrive concurrently). A 4xx is the
 * server refusing the value; resending it only repeats the refusal.
 */
export const isRetryableApiError = (e: any): boolean => {
  const status = getApiErrorStatus(e)
  return status === undefined || status >= 500
}

/** The HTTP status of an API error, on the axios shape or a normalised one. */
export const getApiErrorStatus = (e: any): number | undefined => {
  const status = e?.response?.status ?? e?.statusCode ?? e?.status
  return typeof status === 'number' ? status : undefined
}

/** Longest plain-text body shown as is; anything longer is not a message. */
const MAX_TEXT_BODY_LENGTH = 300

/**
 * A string body that reads as a message. A gateway answering 502 / 504 sends
 * its own HTML error page, which became a toast full of markup; such a body
 * (or one too long to be a sentence) is skipped for the fallbacks below.
 */
const readableTextBody = (data: unknown): string | null => {
  if (typeof data !== 'string') return null
  const text = data.trim()
  if (!text || text.length > MAX_TEXT_BODY_LENGTH) return null
  if (/^<|<\/?(html|head|body|title|h1|center|div|p)\b/i.test(text)) return null
  return text
}

export const getApiErrorMessage = (e: any, fallback?: string): string => {

  const data = e?.originalError?.response?.data ?? e?.response?.data

  const textBody = readableTextBody(data)
  if (textBody) return textBody
  // A body that is markup carries nothing to read; past here it is treated
  // as absent, and the caller's fallback or axios's status line is used.

  // The field reason only when the message is the validator's generic line.
  // Other 400s put codes in `errors` (`srcErrorCode: ["2001"]` beside
  // "object exists"), where the message is the useful part.
  // From the body when there is one: on an axios error `e.message` is axios's
  // own "Request failed with status code 400", not the server's words.
  const message = e?.response ? (data?.Message ?? data?.message) : e?.message
  const generic =
    !message || /one or more validation errors occurred/i.test(String(message))
  const fieldError = generic ? firstFieldError(e) : null
  if (fieldError) return fieldError
  const fromPayload = data?.Message ?? data?.message ?? data?.title
  if (typeof fromPayload === 'string' && fromPayload.trim()) return fromPayload

  // A caller's own fallback beats axios's "Request failed with status code N".
  if (fallback) return fallback
  if (typeof e === 'string' && e.trim()) return e
  if (typeof e?.message === 'string' && e.message.trim()) return e.message

  return 'Something went wrong'
}

export class NotifyService {
  static error(
    error: unknown,
    options?: ToastOptions,
    fallbackMessage?: string,
  ) {
    toast.error(normalizeApiError(error, fallbackMessage).message, options)
  }

  static success(response: unknown, options?: ToastOptions) {
    let message = 'Success'

    if (typeof response === 'string') {
      message = response
    } else if (response && typeof response === 'object') {
      const respObj = response as Record<string, any>
      const data = respObj.data
      if (typeof data === 'string' && data.trim()) {
        message = data
      } else if (data && typeof data === 'object') {
        message =
          (typeof data.Message === 'string' && data.Message) ||
          (typeof data.message === 'string' && data.message) ||
          'Success'
      } else if (typeof respObj.Message === 'string' && respObj.Message) {
        message = respObj.Message
      } else if (typeof respObj.message === 'string' && respObj.message) {
        message = respObj.message
      }
    }

    toast.success(message, options)
  }
}
